// @flow
import * as React from 'react';
import { useMemo, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ARCHIVE_STATUS_FILTERS } from '@groupClass/constants';
import IconTrash from '@icons/components/IconTrash';
import IconTick from '@icons/components/IconTick';
import IconCalendar from '@icons/components/IconCalendar';
import IconBars from '@icons/components/IconBars';
import IconTransfer from '@icons/components/IconTransfer';

type Props = {
  value: string,
  onChange: (value: string) => void,
  disabled?: boolean,
  className?: string,
};

const KEY = {
  LEFT: 'ArrowLeft',
  RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End',
};

const GroupClassArchiveStatusFilter = ({
  value,
  onChange,
  className,
}: Props): React$Node => {
  const { t } = useTranslation();
  const wrapRef = useRef<?HTMLDivElement>(null);

  const options = useMemo(
    () => [
      { value: ARCHIVE_STATUS_FILTERS.ALL, labelKey: 'groupClasses.archiveFilters.all', icon: 'grid' },
      { value: ARCHIVE_STATUS_FILTERS.DEFAULT_PAYMENT, labelKey: 'groupClasses.archiveFilters.defaultPayment', icon: 'coin' },
      { value: ARCHIVE_STATUS_FILTERS.NOT_COMPLETED, labelKey: 'groupClasses.archiveFilters.notCompleted', icon: 'clock' },
      { value: ARCHIVE_STATUS_FILTERS.COMPLIANT, labelKey: 'groupClasses.archiveFilters.compliant', icon: 'shield' },
      { value: ARCHIVE_STATUS_FILTERS.TO_REMOVE, labelKey: 'groupClasses.archiveFilters.toRemove', icon: 'trash' },
    ],
    [],
  );

  const currentIndex = Math.max(0, options.findIndex((o) => o.value === value));

  const focusAt = useCallback((idx: number) => {
    const el = wrapRef.current;
    if (!el) return;
    const btns = el.querySelectorAll('button[data-role="seg-item"]');
    const target = btns && btns[idx];
    if (target && target instanceof HTMLElement) target.focus();
  }, []);

  const onKeyDown = useCallback(
    (e: SyntheticKeyboardEvent<HTMLDivElement>) => {
      const last = options.length - 1;
      e.preventDefault();

      if (e.key === KEY.LEFT) {
        const next = currentIndex > 0 ? currentIndex - 1 : last;
        onChange(options[next].value);
        focusAt(next);
      } else if (e.key === KEY.RIGHT) {
        const next = currentIndex < last ? currentIndex + 1 : 0;
        onChange(options[next].value);
        focusAt(next);
      } else if (e.key === KEY.HOME) {
        onChange(options[0].value);
        focusAt(0);
      } else if (e.key === KEY.END) {
        onChange(options[last].value);
        focusAt(last);
      }
    },
    [options, currentIndex, focusAt, onChange],
  );

  return (
    <div className={ clsx('field', className) }>
      <div className="gcaf">
        <div
          ref={ wrapRef }
          role="radiogroup"
          aria-label={ t('groupClasses.archiveFilters.aria', 'Filter by archive status') }
          className="gcaf__grid"
          onKeyDown={ onKeyDown }
        >
          { options.map((opt) => {
            const selected = opt.value === value;
            return (
              <button
                key={ opt.value }
                type="button"
                role="radio"
                data-role="seg-item"
                aria-checked={ selected }
                onClick={ () => onChange(opt.value) }
                className={ clsx('gcaf__btn') }
              >
                <div className="gcaf__row">
                  <span className="gcaf__icon" aria-hidden="true">
                    { opt.icon === 'grid' && <IconBars className="gcaf__filterIcon" /> }
                    { opt.icon === 'coin' && <IconTransfer className="gcaf__filterIcon" /> }
                    { opt.icon === 'clock' && <IconCalendar className="gcaf__filterIcon" /> }
                    { opt.icon === 'shield' && <IconTick className="gcaf__filterIcon" /> }
                    { opt.icon === 'trash' && <IconTrash className="gcaf__filterIcon" /> }
                  </span>
                  <span className="gcaf__labelTitle">{ t(opt.labelKey) }</span>
                </div>

                <input
                  type="radio"
                  name="archive-status"
                  value={ opt.value }
                  checked={ selected }
                  readOnly
                  tabIndex={ -1 }
                  aria-hidden="true"
                  className="is-hidden"
                />
              </button>
            );
          }) }
        </div>
      </div>
    </div>
  );
};

export default GroupClassArchiveStatusFilter;
